import React, { useContext } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { GlobalStateContext } from '../../general/GlobalStateProvider';
import InfoGrid from './InfoGrid';
import UserCard from './UserCard';
import { formatSessionDuration } from './utils';

interface OverviewTabProps {
    session: any;
    registeredUsers: any[];
    sessionDays: string[];
    sessionSummary: any;
    isCoach: boolean;
    currentUser: any;
    loading: boolean;
    onSummaryPress: (userId: string) => void;
}

const OverviewTab: React.FC<OverviewTabProps> = ({
    session,
    registeredUsers,
    sessionDays,
    sessionSummary,
    isCoach,
    currentUser,
    loading,
    onSummaryPress,
}) => {
    const global = useContext(GlobalStateContext);

    if (loading && !session) {
        return (
            <View style={styles.loadingContainer}>
                <MaterialCommunityIcons name="loading" size={32} color="#2C67D6" />
                <Text style={styles.loadingText}>Loading session data...</Text>
            </View>
        );
    }

    if (!session) {
        return (
            <View style={styles.loadingContainer}>
                <MaterialCommunityIcons name="alert-circle" size={32} color="#FF6B6B" />
                <Text style={styles.loadingText}>Session not found</Text>
            </View>
        );
    }

    return (
        <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}>
            {/* Session Info Cards */}
            <InfoGrid 
                registeredCount={registeredUsers.length}
                durationLabel={formatSessionDuration(session?.startDate, session?.endDate)}
                priceLabel={`$${session?.price || '0'}`}
                capacityLabel={`${session?.capacity || '0'}`}
            />

            {/* Camper overview list: show only the current student */}
            {!isCoach && (
                <View style={styles.section}>
                    {(() => {
                        const parentId = global?.user?.userId || currentUser?.userId;
                        const activeChildId = global?.activeChild?.childId || global?.user?.activeChildId;
                        let camper = null as any;
                        
                        if (activeChildId) {
                            camper = registeredUsers.find((u: any) => 
                                u.childId === activeChildId || u.userId === activeChildId
                            );
                        }
                        if (!camper && parentId) {
                            camper = registeredUsers.find((u: any) => u.parentId === parentId);
                        }
                        if (!camper) return null;

                        return (
                            <View style={styles.usersList}>
                                <UserCard
                                    user={camper}
                                    sessionDays={sessionDays}
                                    attendance={sessionSummary.attendance}
                                    onSummaryPress={onSummaryPress}
                                    showSummaryButton={true}
                                />
                            </View>
                        );
                    })()}
                </View>
            )}

            {/* Registered Users List - Only show for coaches */}
            {isCoach && (
                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>Registered Campers</Text>
                    {registeredUsers.length > 0 ? (
                        <ScrollView style={styles.usersListContainer} showsVerticalScrollIndicator={false}>
                            <View style={styles.usersList}>
                                {registeredUsers.map((user) => (
                                    <UserCard
                                        key={user.userId}
                                        user={user}
                                        sessionDays={sessionDays}
                                        attendance={sessionSummary.attendance}
                                    />
                                ))}
                            </View>
                        </ScrollView>
                    ) : (
                        <View style={styles.emptyState}>
                            <MaterialCommunityIcons name="account-off" size={48} color="#ccc" />
                            <Text style={styles.emptyStateText}>No campers registered yet</Text>
                        </View>
                    )}
                </View>
            )}
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    tabContent: {
        flex: 1,
        padding: 24,
    },
    section: {
        marginBottom: 32,
    },
    sectionTitle: {
        fontSize: 20,
        fontWeight: '700',
        color: '#1a1a1a',
        marginBottom: 16,
    },
    usersList: {
        gap: 12,
        display: 'flex',
        flexDirection: 'column',
    },
    usersListContainer: {
        maxHeight: 300,
        flex: 1,
    },
    emptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 60,
    },
    emptyStateText: {
        fontSize: 16,
        color: '#999',
        marginTop: 16,
        textAlign: 'center',
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40,
    },
    loadingText: {
        fontSize: 16,
        color: '#666',
        marginTop: 12,
        textAlign: 'center',
    },
});

export default OverviewTab;

